import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, Loader2, FileText, Upload as UploadIcon, Download, Calendar, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ResumeItem {
    id: string;
    name: string;
    format: string;
    url: string;
    lastUpdated: string;
    size: string;
    icon: string;
}

export default function ResumeManager() {
    const [items, setItems] = useState<ResumeItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);

    const [newItem, setNewItem] = useState<Partial<ResumeItem>>({
        name: 'Standard Resume',
        format: 'PDF',
        url: '',
        icon: 'FileText'
    });

    const [file, setFile] = useState<File | null>(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const { data, error } = await supabase
                .from('site_content')
                .select('content')
                .eq('section_key', 'about_resume')
                .maybeSingle();

            if (error) throw error;
            if (data && data.content) {
                setItems(data.content);
            }
        } catch (err) {
            console.error('Error fetching resumes:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async () => {
        if (!file) return null;

        setUploading(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `resumes/${Math.random().toString(36).substring(2)}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
                .from('portfolio-assets')
                .upload(fileName, file);

            if (uploadError) throw uploadError;

            const { data } = supabase.storage
                .from('portfolio-assets')
                .getPublicUrl(fileName);

            return data.publicUrl;
        } catch (error: any) {
            console.error('Error uploading file:', error);
            alert('Failed to upload file');
            return null;
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async (updatedList: ResumeItem[]) => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('site_content')
                .upsert({
                    section_key: 'about_resume',
                    content: updatedList,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            setItems(updatedList);
            setIsFormOpen(false);
            setNewItem({ name: 'Standard Resume', format: 'PDF', url: '', icon: 'FileText' });
            setFile(null);
        } catch (err) {
            console.error('Error saving resumes:', err);
            alert('Failed to save resumes');
        } finally {
            setSaving(false);
        }
    };

    const addItem = async () => {
        if (!newItem.name) return;

        let fileUrl = newItem.url;

        if (file) {
            fileUrl = (await handleFileUpload()) as string;
            if (!fileUrl) return;
        }

        if (!fileUrl) {
            alert('Please upload a file or provide a URL');
            return;
        }

        const itemToAdd: ResumeItem = {
            id: Math.random().toString(36).substr(2, 9),
            name: newItem.name!,
            format: newItem.format || 'PDF',
            url: fileUrl,
            lastUpdated: new Date().toLocaleDateString(),
            size: file ? `${(file.size / 1024).toFixed(0)} KB` : 'N/A',
            icon: newItem.icon || 'FileText'
        };

        const updated = [...items, itemToAdd];
        handleSave(updated);
    };

    const deleteItem = (id: string) => {
        if (!confirm('Delete this resume?')) return;
        const updated = items.filter(i => i.id !== id);
        handleSave(updated);
    };

    if (loading) return <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-blue-500 w-8 h-8" /></div>;

    return (
        <div className="space-y-10 max-w-6xl mx-auto py-10">
            <div className="flex justify-between items-center glass-vibrant rounded-[2rem] p-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-cyan-500 shadow-[0_0_15px_rgba(0,210,255,0.8)]" />
                <div>
                    <h2 className="text-3xl font-black text-white mb-2 tracking-tight">Vaulted <span className="text-cyan-400">Documents</span></h2>
                    <p className="text-xs font-black text-gray-500  tracking-widest">Digital asset synchronization protocol</p>
                </div>
                <button
                    onClick={() => setIsFormOpen(!isFormOpen)}
                    className="btn-vibrant-cyan"
                >
                    {isFormOpen ? 'Disconnect' : <><Plus size={20} className="inline mr-2" /> Initialize Upload</>}
                </button>
            </div>

            <AnimatePresence>
                {isFormOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="glass-vibrant border-2 border-cyan-500/20 rounded-[2.5rem] p-10 shadow-2xl relative"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-3">
                                <label className="block text-[10px] font-black text-gray-500  tracking-widest">Entry Identifier</label>
                                <input
                                    type="text"
                                    value={newItem.name}
                                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                    className="w-full bg-white/5 border-2 border-white/5 rounded-2xl text-white px-5 py-4 focus:border-cyan-500/50 focus:outline-none transition-all placeholder:text-gray-700"
                                    placeholder="Resume v2.0 - FullStack"
                                />
                            </div>
                            <div className="space-y-3">
                                <label className="block text-[10px] font-black text-gray-500  tracking-widest">Format Protocol</label>
                                <select
                                    value={newItem.format}
                                    onChange={(e) => setNewItem({ ...newItem, format: e.target.value })}
                                    className="w-full bg-white/5 border-2 border-white/5 rounded-2xl text-white px-5 py-4 focus:border-cyan-500/50 focus:outline-none transition-all cursor-pointer"
                                >
                                    <option value="PDF">PDF / ARCHIVE</option>
                                    <option value="Word">DOC / LEGACY</option>
                                    <option value="Online">HTTP / CLOUD</option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-10">
                            <label className="block text-[10px] font-black text-gray-500  tracking-widest mb-4">Transmission Source</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="border-2 border-dashed border-white/10 hover:border-cyan-500/50 rounded-[2rem] p-8 transition-all group flex flex-col items-center justify-center cursor-pointer relative bg-white/2">
                                    <input
                                        type="file"
                                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                                        accept=".pdf,.doc,.docx"
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                    />
                                    <UploadIcon className="text-gray-600 group-hover:text-cyan-400 mb-4 transition-colors p-2 bg-white/5 rounded-xl" size={48} />
                                    <span className="text-xs font-black text-gray-500 group-hover:text-cyan-400  tracking-widest">
                                        {file ? file.name : 'Inject Local Binary'}
                                    </span>
                                </div>
                                <div className="space-y-6">
                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-gray-700  tracking-widest">Remote Endpoint</label>
                                        <input
                                            type="url"
                                            value={newItem.url}
                                            onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                                            className="w-full bg-white/5 border-2 border-white/5 rounded-2xl text-white px-5 py-4 focus:border-cyan-500/50 focus:outline-none transition-all placeholder:text-gray-800"
                                            placeholder="https://cloud.storage/..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-5 mt-10 pt-8 border-t border-white/5">
                            <button
                                onClick={addItem}
                                disabled={saving || uploading || !newItem.name}
                                className="btn-vibrant-cyan flex items-center gap-3"
                            >
                                {(saving || uploading) ? <Loader2 className="animate-spin h-5 w-5" /> : <UploadIcon size={20} />}
                                {uploading ? 'Transmitting...' : saving ? 'Syncing...' : 'Deploy Asset'}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {items.map((item, index) => (
                    <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="glass border-2 border-white/5 rounded-[2.5rem] p-8 flex flex-col gap-8 group hover:border-cyan-500/40 transition-all duration-500 relative overflow-hidden shadow-2xl"
                    >
                        {/* Interactive Aura */}
                        <div className="absolute -top-20 -right-20 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl group-hover:bg-cyan-500/20 transition-all" />

                        <button
                            onClick={() => deleteItem(item.id)}
                            className="absolute top-6 right-6 text-gray-400 hover:text-red-500 transition-all p-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-red-500/30 shadow-sm z-20"
                            title="Delete Document"
                        >
                            <Trash2 size={20} />
                        </button>

                        <div className="flex items-start gap-6">
                            <div className="h-20 w-20 rounded-[1.5rem] bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] transition-all duration-500">
                                <FileText size={36} />
                            </div>
                            <div className="flex-grow pt-2">
                                <h3 className="font-black text-white text-xl leading-tight mb-2 group-hover:text-cyan-400 transition-colors">{item.name}</h3>
                                <div className="flex items-center gap-3">
                                    <span className="bg-cyan-500/10 px-3 py-1 rounded-full text-[10px] text-cyan-400 font-black tracking-widest  border border-cyan-500/20">{item.format}</span>
                                    <span className="text-[10px] font-bold text-gray-500  tracking-widest">{item.size}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 text-[10px] font-bold text-gray-600  tracking-[0.2em]">
                            <Calendar size={14} />
                            <span>Synced: {item.lastUpdated}</span>
                        </div>

                        <div className="flex gap-4">
                            <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 bg-white/5 hover:bg-cyan-600 text-white hover:text-black px-6 py-4 rounded-2xl text-xs font-black  tracking-widest flex items-center justify-center gap-3 transition-all group/btn border border-white/5 hover:border-cyan-400 shadow-[0_0_15px_rgba(0,0,0,0.3)]"
                            >
                                <Download size={18} className="group-hover/btn:translate-y-1 transition-transform" />
                                Access
                            </a>
                            <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-4 bg-white/2 hover:bg-white/10 text-gray-500 hover:text-white rounded-2xl transition-all border border-white/5"
                            >
                                <ExternalLink size={24} />
                            </a>
                        </div>
                    </motion.div>
                ))}

                {items.length === 0 && !loading && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="col-span-full py-20 text-center flex flex-col items-center gap-4 glass-vibrant border-dashed border-cyan-500/20 rounded-[2.5rem]"
                    >
                        <div className="p-4 bg-cyan-500/5 rounded-full border border-cyan-500/10">
                            <FileText size={48} className="text-cyan-400/30" />
                        </div>
                        <div className="space-y-1">
                            <p className="text-xl font-black text-white  tracking-tighter">No Archive Entries</p>
                            <p className="text-xs font-bold text-gray-600  tracking-widest">Connect your digital assets above</p>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
